import assign from 'lodash/assign';
import has from 'lodash/has';
import startsWith from 'lodash/startsWith';
import endsWith from 'lodash/endsWith';
import trimStart from 'lodash/trimStart';
import trimEnd from 'lodash/trimEnd';
import toInteger from 'lodash/toInteger';

import {
  logoutFromGoogle,
  loadUnreadNotifications,
  logout,
  setRequestInProcess,
  setError,
} from '../actions';
import { getAccessToken, getOriginalUser, getCurrentUser } from '../reducers/selectors';

import {
  NEX_GTOKEN_AUTH_REQUEST,
  NEX_USERNAME_AUTH_REQUEST,
  NEX_REFRESH_AUTH_REQUEST,
  NEX_LOGOUT_REQUEST,
} from '../constants/actionTypes';

import AnalyticsUtils from '../lib/AnalyticsUtils';
import JSONUtils from '../lib/JSONUtils';

function isEmbeddedSession() {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return new URLSearchParams(window.location.search).get('embedded') === 'true';
  } catch {
    return false;
  }
}

import { API_SERVER_URL, CALL_PRIVATE_API, CALL_AUTH_API, CALL_GENAI_API } from 'js/constants/api';
export { CALL_AUTH_API, CALL_PRIVATE_API, CALL_GENAI_API };

import { callAuthAPI } from './api/authApi';
import { callPrivateAPI } from './api/adminApi';
import { getFastApiBaseUrl } from './api/fastApi';
import { getGenAIBaseUrl } from './api/genaiApi';

export default (store) => (next) => (action) => {
  // Determine which API type is being called
  const getAPIType = () => {
    if (action?.[CALL_GENAI_API]) return CALL_GENAI_API;
    if (action?.[CALL_PRIVATE_API]) return CALL_PRIVATE_API;
    if (action?.[CALL_AUTH_API]) return CALL_AUTH_API;
    return null;
  };

  const apiType = getAPIType();

  // Skip if not an API action
  if (!apiType) {
    return next(action);
  }

  let callAPIAction = null;
  let callAPIMethod = null;
  let isGenAIAPI = false;
  let isAuthAPI = false;

  switch (apiType) {
    case CALL_GENAI_API:
      isGenAIAPI = true;
      callAPIAction = action[CALL_GENAI_API];
      callAPIMethod = callPrivateAPI;
      break;
    case CALL_PRIVATE_API:
      callAPIAction = action[CALL_PRIVATE_API];
      callAPIMethod = callPrivateAPI;
      break;
    case CALL_AUTH_API:
      isAuthAPI = true;
      callAPIAction = action[CALL_AUTH_API];
      callAPIMethod = callAuthAPI;
      break;
    default:
      return next(action);
  }

  let {
    endpoint,
    schema,
    types,
    requestMethod,
    aclQueryMode,
    payload,
    requestInfo,
    ignoreErrors,
    isMultipart,
    extraHeaders,
  } = callAPIAction;

  if (typeof endpoint === 'function') {
    endpoint = endpoint(store.getState());
  }

  if (typeof endpoint !== 'string') {
    throw new Error('Specify a string endpoint URL');
  }
  //
  // if (!schema) {
  //   throw new Error('Specify a schema for the response');
  // }

  if (!Array.isArray(types) || types.length !== 3) {
    throw new Error('Expected an array of 3 action types');
  }

  if (!types.every((type) => typeof type === 'string')) {
    throw new Error('Expected action types to be strings');
  }

  const actionWith = (data) => {
    const finalAction = assign({}, action, data);
    if (has(finalAction, CALL_PRIVATE_API)) {
      delete finalAction[CALL_PRIVATE_API];
    }
    if (has(finalAction, CALL_AUTH_API)) {
      delete finalAction[CALL_AUTH_API];
    }
    if (has(finalAction, CALL_GENAI_API)) {
      delete finalAction[CALL_GENAI_API];
    }
    return finalAction;
  };

  const [requestType, successType, failureType] = types;
  next(actionWith({ type: requestType, requestInfo }));
  next(setRequestInProcess(true, requestType));

  const isAbsoluteUrl = endpoint.startsWith('http://') || endpoint.startsWith('https://');
  const joinUrl = (base, path) => {
    if (!base) return path;
    if (!path) return base;
    const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${normalizedBase}${normalizedPath}`;
  };

  let fullUrl;
  if (isAuthAPI) {
    // Auth endpoints should always hit Admin API (API_SERVER_URL)
    if (isAbsoluteUrl) {
      fullUrl = endpoint;
    } else {
      fullUrl = joinUrl(API_SERVER_URL, endpoint);
    }
  } else {
    // Determine base URL: GenAI API takes precedence, then FastAPI migration, then default.
    let baseUrl = '';
    if (isGenAIAPI) {
      baseUrl = getGenAIBaseUrl(endpoint);
    } else {
      // TEMPORARY: FastAPI Migration - Replace with API_SERVER_URL once migration is complete
      baseUrl = getFastApiBaseUrl(endpoint, requestMethod);
    }

    fullUrl = isAbsoluteUrl ? endpoint : joinUrl(baseUrl, endpoint);
  }
  const accessToken = getAccessToken(store.getState());
  const currentUser = getCurrentUser(store.getState());
  let org =
    currentUser && has(currentUser, 'org')
      ? currentUser.org
      : currentUser && has(currentUser, 'default_org')
        ? currentUser.default_org
        : null;

  return callAPIMethod(
    fullUrl,
    requestMethod,
    aclQueryMode,
    payload,
    schema,
    accessToken,
    org,
    isMultipart,
    requestType,
    store,
    extraHeaders,
  ).then(
    (response) => {
      let entities = null,
        result = null;

      if (response && has(response, 'rsp') && response.rsp) {
        entities = response.rsp.entities;
        result = response.rsp.result;
      }

      let rspHeaders = has(response, 'rspHeaders') ? response.rspHeaders : {};
      let responsePayload = {
        entities: entities,
        result: result || response,
        receivedAt: Date.now(),
        requestInfo,
        schema,
        rspHeaders,
      };

      if (
        rspHeaders?.['X-Current-Page'] &&
        rspHeaders?.['X-Total-Page-Count'] &&
        rspHeaders?.['X-Total-Count']
      ) {
        responsePayload.currentPage = toInteger(rspHeaders['X-Current-Page']);
        responsePayload.totalPages = toInteger(rspHeaders['X-Total-Page-Count']);
        responsePayload.totalItems = toInteger(rspHeaders['X-Total-Count']);
      }

      next(
        actionWith({
          type: successType,
          ...responsePayload,
        }),
      );
      next(setRequestInProcess(false, requestType));
      if (requestType === NEX_GTOKEN_AUTH_REQUEST || requestType === NEX_USERNAME_AUTH_REQUEST) {
        const currentState = store.getState();
        const thisUser = getOriginalUser(currentState);
        const userOrgId =
          thisUser && has(thisUser, 'org')
            ? thisUser.org
            : thisUser && has(thisUser, 'default_org')
              ? thisUser.default_org
              : null;
        const thisOrg =
          userOrgId && has(currentState, `entities.orgs[${userOrgId}]`)
            ? currentState.entities.orgs[userOrgId]
            : null;

        //if new login, fetch notifications
        AnalyticsUtils.registerUserSession(thisUser, userOrgId, thisOrg);
        store.dispatch(loadUnreadNotifications());
      }

      return responsePayload;
    },
    (error) => {
      let forceLogoutFlag = false;

      // Check for 413 error code from multiple possible locations
      // When nginx returns HTML, error.status.code might be 0, so check error.code directly
      const statusCode =
        (error.status && error.status.code) ||
        error.code ||
        (error.response && error.response.status) ||
        (error.response && error.response.statusCode) ||
        null;

      // Detect 413 errors when nginx returns HTML error page
      // nginx intercepts large requests before Rails processes them
      const isLoadError = has(error, 'error') && error.error === 'loaderror';
      const isEmptyEntity = !error.entity || error.entity === '';
      const isTransformEndpoint = endpoint && endpoint.includes('/transform');
      const is413FromNginx = isLoadError && isEmptyEntity && isTransformEndpoint;

      const is413Error = statusCode === 413 || is413FromNginx;

      // Handle 413 errors FIRST (before parsing, since nginx returns HTML)
      if (is413Error) {
        let errorMessage =
          'The request payload is too large. Please reduce the size of your input data or use fewer samples.';

        // Try to parse JSON response if available (in case backend is configured to return JSON)
        let errorMessageObj =
          has(error, 'entity') && error.entity && error.entity !== ''
            ? JSONUtils.parseJson(error.entity)
            : null;

        // If we got JSON with details, append it
        if (errorMessageObj && errorMessageObj.details) {
          const details = errorMessageObj.details;
          if (typeof details === 'string' && details.trim()) {
            errorMessage = `${errorMessage} ${details}`;
          }
        }

        let errorPayload = {
          type: failureType,
          message: errorMessage,
          error: errorMessage,
          code: 413,
          receivedAt: Date.now(),
          request: {
            endpoint,
            payload,
          },
          requestInfo,
        };
        next(actionWith(errorPayload));
        store.dispatch(setError(errorPayload, ignoreErrors));
        next(setRequestInProcess(false, requestType));
        return;
      }

      // For non-413 errors, continue with normal error handling
      let errorMessageObj =
        has(error, 'entity') && error.entity && error.entity !== ''
          ? JSONUtils.parseJson(error.entity)
          : null;
      let errorMessage = 'Network error';
      if (has(errorMessageObj, 'message')) {
        errorMessage = errorMessageObj.message;
        if (startsWith(errorMessage, '{"message":') && endsWith(errorMessage, '"}')) {
          //this is a custom weird backend format of message.
          //TODO: parse this to look better. should ideally be fixed on the API.

          errorMessage = trimStart(errorMessage, '{"message":"');
          errorMessage = trimEnd(errorMessage, '"}');
        } else if (errorMessage === 'Unable to authenticate') {
          /*
            NEX-17445:
            If the message is equal to "Unable to authenticate" and
            details has some content after stripping out the pattern "Probe Service: /authenticate/<connector_code>/ " and (NAP-XYZ) then display that content.

            Example API response that needs to be handled:
                {
                    "status": 403,
                    "message": "Unable to authenticate",
                    "details": "Probe Service: /authenticate/rest/ actual reason why this failed. (NAP-00048)"
                }
          */

          if (errorMessageObj && errorMessageObj.details) {
            const details = errorMessageObj.details;
            // Ensure details is a string before calling match
            if (typeof details === 'string') {
              const probeServiceMatch = details.match(/Probe Service: [^:]*\//);
              if (probeServiceMatch) {
                let extractedErrorMessage = details.replace(probeServiceMatch[0], '');
                extractedErrorMessage = extractedErrorMessage.replace(/\(NAP-\d+\)/, '');
                extractedErrorMessage = extractedErrorMessage.trim();

                // If we successfully extracted a meaningful error message, use it
                if (extractedErrorMessage && extractedErrorMessage !== 'Unable to authenticate') {
                  errorMessage = extractedErrorMessage;
                }
              }
            }
          }
        }
      } else if (statusCode && statusCode >= 400) {
        let codeBasedReason = '';
        let statusText = (error.status && error.status.text) || '';

        switch (statusCode) {
          case 401:
            statusText = '';
            codeBasedReason = 'Access is denied due to invalid credentials';
            break;
          case 403:
            statusText = '';
            codeBasedReason = 'You do not have necessary permissions to access this resource';
            break;
          case 404:
            statusText = '';
            codeBasedReason = 'Resource not found';
            break;
          case 413:
            // This case should rarely be reached since we handle 413 above,
            // but keeping it as a fallback
            statusText = '';
            codeBasedReason =
              'The request payload is too large. Please reduce the size of your input data or use fewer samples.';
            // Check if API response has additional details
            if (errorMessageObj && errorMessageObj.details) {
              const details = errorMessageObj.details;
              if (typeof details === 'string' && details.trim()) {
                codeBasedReason = `${codeBasedReason} ${details}`;
              }
            }
            break;

          default:
            if (statusCode >= 500) {
              console.log(error);
              codeBasedReason = 'Sorry, there was an error in completing your request.';
            } else if (statusCode >= 400) {
              codeBasedReason = 'Sorry, this request could not be completed.';
            }
        }

        errorMessage = `${statusText} ${codeBasedReason}`;
      } else if (has(error, 'error') && error.error === 'loaderror') {
        // If we already detected this as a 413 error, don't override the message
        if (!is413FromNginx) {
          errorMessage = 'Sorry, there was an error in completing your request.';
        }
        // forceLogoutFlag = true;
      } else {
        errorMessage = 'Sorry, there was an error in completing your request.';
      }

      // Determine the final status code
      let finalStatusCode = statusCode;
      if (is413FromNginx) {
        finalStatusCode = 413;
      } else if (!finalStatusCode) {
        finalStatusCode = (error.status && error.status.code) || error.code;
      }

      let errorPayload = {
        type: failureType,
        message: errorMessage,
        error: error.text || errorMessage,
        code: finalStatusCode,
        receivedAt: Date.now(),
        request: {
          endpoint,
          payload,
        },
        requestInfo,
      };
      next(actionWith(errorPayload));
      //TODO: looks like we need store.dispatch for an async call here. can we replace it?
      store.dispatch(setError(errorPayload, ignoreErrors));
      next(setRequestInProcess(false, requestType));

      if (
        // forceLogoutFlag ||
        requestType === NEX_REFRESH_AUTH_REQUEST ||
        (callAPIAction &&
          apiType === CALL_PRIVATE_API &&
          requestType !== NEX_LOGOUT_REQUEST &&
          error?.status?.code === 401 &&
          fullUrl.startsWith(API_SERVER_URL))
      ) {
        // Embedded Express/Veda iframes receive tokens from the parent window.
        // A background 401 (expired token, race during auth) must not logout and
        // wipe the session — that causes auth/prepare loops in the iframe.
        if (!isEmbeddedSession()) {
          store.dispatch(logout());
        }
      } else if (requestType === NEX_GTOKEN_AUTH_REQUEST) {
        //if google auth, logout from google
        store.dispatch(logoutFromGoogle());
      }

      //TODO: we need promise reject if this is a chained api call, but will throw console error if no one is catching this error.
      return Promise.reject(errorPayload);
    },
  );
};
